import { WorkoutExercise } from '../../types';
import { BaseApiService } from './BaseApiService';

/**
 * Workout Session Service
 * Manages the current workout session (today's workout)
 * Persists to backend via /api/workout-session
 */
export class WorkoutSessionService extends BaseApiService {
  private static readonly ANON_STORAGE_KEY = 'gym_planner_anonymous_workout_session';

  /**
   * Get current user ID from localStorage
   */
  private getCurrentUserId(): string | null {
    try {
      if (typeof window === 'undefined' || !('localStorage' in window)) {
        return null;
      }

      const userStr = localStorage.getItem('gym_planner_user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.id || null;
      }
    } catch (error) {
      console.error('Error getting current user ID for workout session:', error);
    }

    return null;
  }

  /**
   * Get current workout exercises from backend
   */
  async getExercises(): Promise<WorkoutExercise[]> {
    const userId = this.getCurrentUserId();
    if (!userId) {
      try {
        const raw = localStorage.getItem(WorkoutSessionService.ANON_STORAGE_KEY);
        return raw ? (JSON.parse(raw) as WorkoutExercise[]) : [];
      } catch (error) {
        console.error('Failed to load anonymous workout session from localStorage:', error);
        return [];
      }
    }

    try {
      console.log('🔄 [WORKOUT-SESSION-FRONTEND] Fetching exercises for userId:', userId);
      const response = await this.get<WorkoutExercise[]>(`/workout-session?userId=${encodeURIComponent(userId)}`);
      console.log('📥 [WORKOUT-SESSION-FRONTEND] Received response from backend:', response);
      const exercises = response || [];
      console.log('📤 [WORKOUT-SESSION-FRONTEND] Returning exercises to UI:', exercises);
      return exercises;
    } catch (error) {
      console.error('💥 [WORKOUT-SESSION-FRONTEND] Failed to load workout session from backend:', error);
      return [];
    }
  }

  /**
   * Set workout exercises in backend
   */
  async setExercises(exercises: WorkoutExercise[]): Promise<void> {
    const userId = this.getCurrentUserId();
    if (!userId) {
      try {
        localStorage.setItem(WorkoutSessionService.ANON_STORAGE_KEY, JSON.stringify(exercises));
      } catch (error) {
        console.error('Failed to save anonymous workout session to localStorage:', error);
      }
      return;
    }

    try {
      console.log('💾 [WORKOUT-SESSION-FRONTEND] Sending exercises to backend:', exercises);
      await this.put('/workout-session', { userId, exercises });
      console.log('✅ [WORKOUT-SESSION-FRONTEND] Backend save completed successfully');
    } catch (error) {
      console.error('Failed to save workout session to backend:', error);
      throw error; // Re-throw so caller knows it failed
    }
  }

  /**
   * Add an exercise to the workout (helper)
   */
  async addExercise(exercise: WorkoutExercise): Promise<void> {
    const current = await this.getExercises();
    await this.setExercises([...current, exercise]);
  }

  /**
   * Remove an exercise from the workout (helper)
   */
  async removeExercise(exerciseId: string): Promise<void> {
    const current = await this.getExercises();
    const next = current.filter(ex => ex.id !== exerciseId);
    await this.setExercises(next);
  }

  /**
   * Update an exercise in the workout (helper)
   */
  async updateExercise(exerciseId: string, updates: Partial<WorkoutExercise>): Promise<void> {
    const current = await this.getExercises();
    const next = current.map(ex => (ex.id === exerciseId ? { ...ex, ...updates } : ex));
    await this.setExercises(next);
  }

  /**
   * Clear the workout session in backend
   */
  async clearSession(): Promise<void> {
    const userId = this.getCurrentUserId();
    if (!userId) {
      try {
        localStorage.removeItem(WorkoutSessionService.ANON_STORAGE_KEY);
      } catch (error) {
        console.error('Failed to clear anonymous workout session from localStorage:', error);
      }
      return;
    }

    try {
      await this.delete(`/workout-session?userId=${encodeURIComponent(userId)}`);
    } catch (error) {
      console.error('Failed to clear workout session in backend:', error);
    }
  }

  /**
   * Check if workout session is empty
   */
  async isEmpty(): Promise<boolean> {
    const current = await this.getExercises();
    return current.length === 0;
  }
}
